export const DEFAULT_APP_TIMEZONE = "America/New_York";

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_APP_TIMEZONE;
  } catch {
    return DEFAULT_APP_TIMEZONE;
  }
};

export const resolveDisplayTimezone = (preferredTimezone) =>
  preferredTimezone || getBrowserTimezone() || DEFAULT_APP_TIMEZONE;

export const parseDateValue = (value) => {
  if (!value) return null;
  let parsed = null;
  if (value instanceof Date) {
    parsed = value;
  } else if (typeof value === "string") {
    const normalized = value.trim();
    const looksLikeNaiveIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(normalized);
    parsed = new Date(looksLikeNaiveIso ? `${normalized}Z` : normalized);
  } else {
    parsed = new Date(value);
  }
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateTimeDisplay = (value, timezone, options = {}) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "";
  const {
    year = "numeric",
    month = "short",
    day = "numeric",
    hour = "2-digit",
    minute = "2-digit",
    ...rest
  } = options;
  return parsed.toLocaleString(undefined, {
    ...(year == null ? {} : { year }),
    ...(month == null ? {} : { month }),
    ...(day == null ? {} : { day }),
    ...(hour == null ? {} : { hour }),
    ...(minute == null ? {} : { minute }),
    ...rest,
    timeZone: resolveDisplayTimezone(timezone),
  });
};

export const formatTimeDisplay = (value, timezone, options = {}) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "";
  return parsed.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    ...options,
    timeZone: resolveDisplayTimezone(timezone),
  });
};

export const formatDateDisplay = (value, timezone, options = {}) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "";
  const {
    year = "numeric",
    month = "short",
    day = "numeric",
    ...rest
  } = options;
  return parsed.toLocaleDateString(undefined, {
    ...(year == null ? {} : { year }),
    ...(month == null ? {} : { month }),
    ...(day == null ? {} : { day }),
    ...rest,
    timeZone: resolveDisplayTimezone(timezone),
  });
};

export const toDateTimeLocalInputValue = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "";
  const local = new Date(parsed.getTime() - (parsed.getTimezoneOffset() * 60000));
  return local.toISOString().slice(0, 16);
};

export const dateTimeLocalInputToIso = (value) => {
  if (!value) return "";
  const match = String(value).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  );
  if (!match) return "";

  const [, yearText, monthText, dayText, hourText, minuteText, secondText = "0", millisecondText = "0"] = match;
  const year = Number(yearText);
  const month = Number(monthText) - 1;
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const millisecond = Number(millisecondText.padEnd(3, "0"));
  const parsed = new Date(year, month, day, hour, minute, second, millisecond);

  const componentsMatch = parsed.getFullYear() === year
    && parsed.getMonth() === month
    && parsed.getDate() === day
    && parsed.getHours() === hour
    && parsed.getMinutes() === minute
    && parsed.getSeconds() === second;
  return componentsMatch ? parsed.toISOString() : "";
};

export const dateTimeLocalInputElementToIso = (input, fallbackValue = "") => {
  const normalizedValue = input?.value || fallbackValue;
  const parsedValue = dateTimeLocalInputToIso(normalizedValue);
  if (parsedValue) return parsedValue;

  const wallClockMilliseconds = Number(input?.valueAsNumber);
  if (!Number.isFinite(wallClockMilliseconds)) return "";

  // datetime-local valueAsNumber represents wall-clock components in UTC.
  // Rebuild those components in the browser timezone before producing an instant.
  const wallClock = new Date(wallClockMilliseconds);
  const local = new Date(
    wallClock.getUTCFullYear(),
    wallClock.getUTCMonth(),
    wallClock.getUTCDate(),
    wallClock.getUTCHours(),
    wallClock.getUTCMinutes(),
    wallClock.getUTCSeconds(),
    wallClock.getUTCMilliseconds(),
  );
  return Number.isNaN(local.getTime()) ? "" : local.toISOString();
};
